/**
 * The day dock's plumbing: which day is open, in which host, how it is left, and where focus goes
 * when it is.
 *
 * ── It lives in this slice since 2 October 2026, and WHERE the open day is kept is a parameter ──
 * It was `pages/plan-builder`'s own hook and read the builder's address itself. The Buddy page
 * (`pages/ai-plan-builder`) mounts the same dock over a draft, with its own address, so the hook
 * moved down with the box it drives (`ui/DayPanelHost`) and takes a `DayDockLocation`: the page's
 * answer to which day is open, in which box, and how each move is written. Everything below is as
 * it was; "the URL" in these notes is the host page's address, which is where both hosts keep it,
 * and the remembered default (`model/dockDefault`) is the builder's, behind `defaultDock` and
 * `switchHost`.
 *
 * ── The URL owns the dock; this hook owns nothing that could disagree with it ────────────────
 * `daySlotToken` and `dock` are read straight out of the address, never mirrored into state. That is
 * what makes the browser's Back button collapse the dock for free, what makes a link to a day work,
 * and what makes a hand-edited `?dock=` land on a validated value rather than on a crash. The
 * pieces of local state below are the ones the address has no business carrying: which block the
 * library is being asked for, how many times it has been asked, whether its drawer is out, and the
 * last thing worth announcing.
 *
 * ── Focus return is OURS, not the dialog's ───────────────────────────────────────────────────
 * Base UI restores focus to whatever opened the popup, which is right until the opener is not there
 * to take it. The week canvas stays MOUNTED in both boxes — behind the modal's scrim, beside the
 * panel — so "the canvas is unmounted in fullscreen", which this note claimed until 13 September
 * 2026, was never the reason and is withdrawn. The card itself is what may be gone: a Cmd+Z that
 * removes the phase, or a day deleted from under an open dock, unmounts the very button Base UI
 * would restore to, and under the modal the week is `aria-hidden` and untouchable at the moment the
 * restore fires. So the dock declares `finalFocus={false}` and the page names the day instead — the
 * workout's own CARD takes focus when it re-mounts and recognises its token. One mechanism, both
 * boxes, and it survives the card being absent.
 *
 * The card rather than a control inside it, since 7 September 2026: the "Programuj dzień" strip that
 * used to be the opener is gone (the mock opens the panel by clicking the card body), so the card is
 * what the trainer opened the day from. It carries `tabIndex={-1}` for exactly this.
 *
 * The day it names is read straight out of the address at the moment of the close, so `close` needs
 * to remember nothing: whether the trainer pressed ✕, Escape, or clicked outside, the dock was open
 * on exactly one day and the URL is what says which.
 *
 * ── The HOST is a remembered default; the open day is still the URL's ───────────────────────
 * `open` takes the host from `model/dockDefault` rather than always answering `panel`, and
 * `switchHost` is the one toggle in the head that writes it. That is the owner's requirement that the choice STICKS: press maximise once and every
 * following day opens in the modal until the restore sends them back.
 *
 * `switchHost` REPLACES the address rather than pushing it. Opening a day is a move to somewhere
 * new, so Back should collapse it; changing which box that same day is drawn in is not — a history
 * entry there would make Back put the day back in the other host instead of closing it, twice for
 * one day.
 *
 * ── The library is a DRAWER in both hosts ───────────────────────────────────────────────────
 * `requestLibrary` used to answer two ways: open the drawer in the panel, and switch `?dock=` to
 * `full` otherwise, where the rail stood beside the day as a 460px column. That column is gone with
 * the old fullscreen host (13 September 2026) — the owner's modal is this panel's own chrome in a
 * bigger box, and the library slides over it exactly as it does at 574px. So there is one answer
 * now, and the branch that carried the other one is deleted rather than left to be read as a mode
 * that still exists.
 *
 * The drawer is the one piece of dock state the URL does not carry, deliberately: it is a transient
 * reach for the catalogue rather than a place, and a link to it would restore a panel with a library
 * hanging over the day. Everything that ENDS the reach closes it — leaving the day, stepping to
 * another one, switching host, and, since 7 September 2026, a successful add.
 *
 * ── An add out of the drawer ends the reach, and focus has to land somewhere ─────────────────
 * The drawer lies modally over the day, so a row cannot be DRAGGED out of it — the form behind is
 * `inert` and a release lands on nothing. Pressing is the whole path, which is also what the mock
 * does: its own library cards carry a `draggable` attribute with no handler behind it. The press
 * having worked, the catalogue is in the way of the thing it just changed, so the drawer goes.
 *
 * That unmounts the row the trainer pressed. Inside a focus trap, focus then falls to `<body>` and
 * the next Tab restarts at the top of the panel — so `exerciseAdded` names the card that was made
 * and it claims focus, the same mechanism `returnFocusToken` already uses for a day card. The token
 * is cleared by every act that could unmount that card, so it can never steal focus later.
 *
 * ── Putting the catalogue AWAY has to land focus too, and it lands on the opener ─────────────
 * Every exit from the drawer that does not add anything — Escape from the search field, Escape from
 * a rail row, the scrim, the drawer's own ✕ — used to leave `document.activeElement` on `<body>`.
 * Inside the modal's trap the next Tab then restarts at the head's first control, so a trainer who
 * looked at the catalogue and changed their mind walked the whole head again to get back to the
 * "+ Ćwiczenie" they had just pressed.
 *
 * So `requestLibrary` remembers the control it was reached from and `closeLibrary` gives focus back
 * to it. The OPENER ELEMENT rather than a token, unlike `focusExerciseToken` and `returnFocusToken`
 * beside it: those two name things that are UNMOUNTED at the moment they are remembered and remount
 * later, which is what a token is for. An opener is standing the whole time — the form behind the
 * drawer is `inert`, not gone — and there are four of them with no identity of their own to name
 * (the day's floating "+ Ćwiczenie", the empty day's twin of it, the warm-up run's pill, and one
 * "Rozgrzewka" chip per exercise card). Threading a fresh id through `plan-day-programming`'s
 * `onRequestLibrary`, and through the two calendar docks that also call it, would buy nothing the
 * element does not already say.
 *
 * Two conditions on it, and both are deliberate. It is taken only when the active element is a
 * BUTTON — a pointer press in Safari focuses no button, and the popup itself would otherwise be
 * recorded as its own opener — and it is returned to only if the element is still connected. Every
 * other way of ending the reach clears it, because each of those has focus of its own to place: an
 * add names the new card, a host switch keeps the toggle, leaving the day names the day's card.
 *
 * The return is an EFFECT rather than a line in the handler: `inert` comes off the form in the same
 * commit that closes the drawer, and `focus()` on an inert subtree is refused silently.
 *
 * ── The live region's one wrinkle ────────────────────────────────────────────────────────────
 * A live region announces when its TEXT CHANGES, so adding two exercises with the same name in a
 * row would announce once. The second identical message is stored with a trailing space: invisible
 * on screen, a different string to the accessibility tree, and it re-announces. The alternative —
 * a counter rendered beside the words — would be read out.
 */

import { useEffect, useRef, useState } from 'react';

import type { AuthorableExerciseRole } from '~/entities/plan-draft';

/** The two boxes a day can be drawn in: the side panel, or the modal over the page. */
export type DayDockMode = 'panel' | 'full';

/**
 * Where the host page keeps the open day, and how each move is written there.
 *
 * The page's address in both hosts. `open` is a move to somewhere new (a history entry, so Back
 * closes the dock); `select`, `close` and `switchHost` rewrite the address in place. `defaultDock`
 * is asked at the moment of the press, and `switchHost` is where a page that remembers the choice
 * writes it BEFORE the address moves. A page with one box answers `'panel'` and ignores the switch.
 */
export interface DayDockLocation {
  readonly daySlotToken: string | null;
  readonly dock: DayDockMode | null;
  readonly defaultDock: () => DayDockMode;
  readonly open: (daySlotToken: string, dock: DayDockMode) => void;
  readonly select: (daySlotToken: string) => void;
  readonly close: () => void;
  readonly switchHost: (next: DayDockMode) => void;
}

export interface DayDockState {
  /** The day the dock is open on, or `null` when it is closed. */
  readonly daySlotToken: string | null;
  readonly dock: DayDockMode | null;
  /** Which block the rail's next insert lands in. */
  readonly libraryRole: AuthorableExerciseRole;
  /**
   * The exercise the rail's next PRESS becomes the warm-up of, or `null` for an ordinary reach.
   *
   * It is the card head's `Rozgrzewka` chip and nothing else, and it is cleared by every other way
   * of asking for the library — a stale target would put the next "+ Ćwiczenie" pick inside a
   * mini-group above a movement the trainer was not looking at.
   */
  readonly libraryWarmupTarget: string | null;
  /** Changes each time the library is asked for, so the rail can take focus again. */
  readonly focusSignal: number;
  /** Whether the library drawer is over the day form. The same drawer in both hosts. */
  readonly isLibraryOpen: boolean;
  /** The last announcement. Rendered into the dock's always-mounted live region. */
  readonly announcement: string;
  /** The day whose card should take focus back — see the header. `null` most of the time. */
  readonly returnFocusToken: string | null;
  /** The exercise whose card should take focus — the one an add out of the drawer just created. */
  readonly focusExerciseToken: string | null;
  /**
   * Open the dock on a day, in the host the trainer last chose. Pushes a history entry, so Back
   * closes it.
   *
   * The default is read at the moment of the press rather than held in state: a second tab that
   * maximised a day writes the same key, and reading it late is what makes this tab agree with it
   * on the next press instead of on the next reload.
   */
  readonly open: (daySlotToken: string, dock?: DayDockMode) => void;
  /** Move the open dock to another day. Replaces: stepping days is looking around one plan. */
  readonly selectDay: (daySlotToken: string) => void;
  readonly close: () => void;
  /**
   * Draw the open day in the other host, and remember that choice for the next day.
   *
   * REPLACES the address: the day did not change, only the box around it, and a history entry here
   * would make Back restore the previous host rather than close the day.
   */
  readonly switchHost: (next: DayDockMode) => void;
  /**
   * Ask for the library, aimed at a block — or, with a token, at ONE exercise, so the pick becomes
   * that movement's own warm-up. Opens the drawer over the day, in either host.
   */
  readonly requestLibrary: (
    role: AuthorableExerciseRole,
    targetExerciseSlotToken: string | null,
  ) => void;
  /**
   * Put the drawer away and give focus back to the control it was opened from. The scrim over the
   * form, the drawer's own ✕ and the Escape `DayDock` intercepts all call it.
   */
  readonly closeLibrary: () => void;
  /**
   * An exercise was added from the library DRAWER. Ends the reach and points focus at the new card.
   */
  readonly exerciseAdded: (exerciseSlotToken: string) => void;
  readonly announce: (message: string) => void;
}

export function useDayDock(location: DayDockLocation): DayDockState {
  const [libraryRole, setLibraryRole] = useState<AuthorableExerciseRole>('Main');
  const [libraryWarmupTarget, setLibraryWarmupTarget] = useState<string | null>(null);
  const [focusSignal, setFocusSignal] = useState(0);
  const [announcement, setAnnouncement] = useState('');
  const [isLibraryOpen, setLibraryOpen] = useState(false);
  const [returnFocusToken, setReturnFocusToken] = useState<string | null>(null);
  const [focusExerciseToken, setFocusExerciseToken] = useState<string | null>(null);
  // The control the library was reached from — see the header. Written in event handlers only.
  const libraryOpener = useRef<HTMLElement | null>(null);

  // Focus goes back to the opener once the drawer is away and the form behind it is no longer
  // `inert`, which is this commit. An open drawer, a mount, and an exit that placed focus itself
  // (each of those cleared the opener) all fall through and do nothing.
  useEffect(() => {
    if (isLibraryOpen) return;
    const opener = libraryOpener.current;
    libraryOpener.current = null;
    if (opener !== null && opener.isConnected) opener.focus();
  }, [isLibraryOpen]);

  // The day the dock is on RIGHT NOW, which is also the card that is owed focus when it closes —
  // whether the close came from the ✕, from Escape, or from a click outside. Read from the address
  // rather than remembered, so there is nothing that could disagree with it.
  const daySlotToken = location.daySlotToken;

  return {
    daySlotToken,
    dock: location.dock,
    libraryRole,
    libraryWarmupTarget,
    focusSignal,
    // Gated on the dock being open at all, so a flag left standing from the last visit cannot slide
    // a library over the first day of the next one.
    isLibraryOpen: isLibraryOpen && location.dock !== null,
    announcement,
    /**
     * Only once the dock is actually CLOSED, which the ADDRESS decides.
     *
     * `close()` sets the token synchronously and the router's update lands a tick later, so for one
     * render the token names a card that is still standing behind an open panel. That card's effect
     * keys on the token and fires exactly once — and while the panel is up the week behind it is
     * `inert`, where `focus()` is refused silently and there is no second chance, because the token
     * does not change again. Withholding it until the address agrees keeps the two in step; the
     * card is focused on the render that also takes the `inert` off.
     */
    returnFocusToken: daySlotToken === null ? returnFocusToken : null,
    focusExerciseToken,
    open: (token, dock = location.defaultDock()) => {
      setReturnFocusToken(null);
      setFocusExerciseToken(null);
      libraryOpener.current = null;
      setLibraryOpen(false);
      location.open(token, dock);
    },
    selectDay: (token) => {
      setFocusExerciseToken(null);
      libraryOpener.current = null;
      setLibraryOpen(false);
      location.select(token);
    },
    close: () => {
      setReturnFocusToken(daySlotToken);
      setFocusExerciseToken(null);
      libraryOpener.current = null;
      setLibraryOpen(false);
      location.close();
    },
    switchHost: (next) => {
      // The catalogue was reached from the box that is being left; it does not travel with the day,
      // and the toggle that was pressed keeps focus, so there is no opener to go back to.
      libraryOpener.current = null;
      setLibraryOpen(false);
      location.switchHost(next);
    },
    requestLibrary: (role, targetExerciseSlotToken) => {
      setLibraryRole(role);
      setLibraryWarmupTarget(targetExerciseSlotToken);
      setFocusSignal((current) => current + 1);
      // The previous add's card is not where focus goes next — the search field is, and the rail
      // takes it on the signal above. A token left standing would fight it the moment that card
      // re-rendered.
      setFocusExerciseToken(null);
      // The control that asked, so putting the catalogue away can hand focus back to it. A BUTTON
      // or nothing — see the header for both conditions.
      const active = globalThis.document.activeElement;
      libraryOpener.current = active instanceof HTMLButtonElement ? active : null;
      // One answer in both hosts: the drawer slides over the day and leaves it where it was. See
      // the header for the branch this used to carry.
      setLibraryOpen(true);
    },
    closeLibrary: () => {
      setLibraryOpen(false);
    },
    exerciseAdded: (exerciseSlotToken) => {
      // The new card takes focus, not the pill that opened the catalogue.
      libraryOpener.current = null;
      setLibraryOpen(false);
      setFocusExerciseToken(exerciseSlotToken);
    },
    announce: (message) => {
      setAnnouncement((current) => (current === message ? `${message} ` : message));
    },
  };
}
